import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxOpen, faCartPlus } from "@fortawesome/free-solid-svg-icons";
import { Button, Card, Col, Form, InputGroup, Row } from "@themesberg/react-bootstrap";
import ContentLoader from "react-content-loader";
import { useHistory } from "react-router-dom";

import { getVendorProducts, getVendorVariants } from "../api/vendor.api";
import { useToast } from "../components/ToastProvider";
import { Routes } from "../routes";
import { resolveAssetUrl } from "../utils/assetUrl";
import { addCartItem } from "../utils/cartStorage";

const INITIAL_PAGE_SIZE = 10;

const sanitizeQuantityInput = (value) => value.replace(/[^\d]/g, "");

const buildInitialSelection = (variants) => {
  return {
    variantId: "",
    quantity: "1"
  };
};

const ProductCardSkeleton = () => (
  <Card border="light" className="shadow-sm h-100 overflow-hidden">
    <ContentLoader
      speed={2}
      viewBox="0 0 200 280"
      backgroundColor="#f3f3f3"
      foregroundColor="#e0e0e0"
    >
      <rect x="0" y="0" width="200" height="200" rx="8" />
      <rect x="10" y="215" width="120" height="12" rx="2" />
      <rect x="10" y="235" width="80" height="10" rx="2" />
      <rect x="10" y="250" width="60" height="10" rx="2" />
      <rect x="10" y="265" width="180" height="32" rx="4" />
    </ContentLoader>
  </Card>
);

export default () => {
  const history = useHistory();
  const { showToast } = useToast();
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [orderSelections, setOrderSelections] = useState({});
  const loadMoreRef = useRef(null);
  const currentProducts = useRef([]);
  const loadingMoreRef = useRef(false);

  const loadVariants = useCallback(async (productIds = []) => {
    try {
      const response = await getVendorVariants({ page: 1, limit: 1000 });
      const allVariants = Array.isArray(response) ? response : [];
      
      if (productIds.length === 0) {
        return allVariants;
      }
      
      return allVariants.filter(variant => 
        productIds.some(pid => Number(variant.product_id) === Number(pid))
      );
    } catch (error) {
      return [];
    }
  }, []);

  const loadProducts = useCallback(async (pageNum = 1, append = false) => {
    if (pageNum === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await getVendorProducts({ page: pageNum, limit: INITIAL_PAGE_SIZE, search: searchValue });
      
      const responseData = response?.data || response || [];
      const newProducts = Array.isArray(responseData) ? responseData : [];
      const total = response?.pagination?.total || response?.total || newProducts.length;
      setTotalProducts(total);

      if (append) {
        setProducts(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const uniqueNew = newProducts.filter(p => !existingIds.has(p.id));
          return [...prev, ...uniqueNew];
        });
      } else {
        setProducts(newProducts);
      }

      const currentLength = append ? currentProducts.current.length + newProducts.length : newProducts.length;
      const shouldLoadMore = newProducts.length >= INITIAL_PAGE_SIZE && total > currentLength;
      setHasMore(shouldLoadMore);

      const productIds = newProducts.map(p => p.id);
      const productVariants = await loadVariants(productIds);

      if (append) {
        setVariants(prev => {
          const existingIds = new Set(prev.map(v => v.id));
          const uniqueNew = productVariants.filter(v => !existingIds.has(v.id));
          return [...prev, ...uniqueNew];
        });
      } else {
        setVariants(productVariants);
      }

      setPage(pageNum);
    } catch (error) {
      showToast({ message: error.message || "Failed to load products", variant: "danger" });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, loadVariants, showToast]);

  useEffect(() => {
    setProducts([]);
    setVariants([]);
    setOrderSelections({});
    setPage(1);
    setHasMore(true);
    loadProducts(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  useEffect(() => {
    const handleScroll = () => {
      if (loadingMoreRef.current || !hasMore) return;
      
      const scrollPosition = window.innerHeight + window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      
      if (documentHeight - scrollPosition < 200) {
        loadProducts(page + 1, true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, page]);

  const productsWithVariants = useMemo(
    () => products.map((product) => ({
      ...product,
      variants: variants.filter((variant) => Number(variant.product_id) === Number(product.id))
    })),
    [products, variants]
  );

  const updateSelection = (productId, field, value) => {
    setOrderSelections((currentSelections) => ({
      ...currentSelections,
      [productId]: {
        ...(currentSelections[productId] || { variantId: "", quantity: "1" }),
        [field]: value
      }
    }));
  };

  const handleAddToCart = (product) => {
    const selection = orderSelections[product.id] || { variantId: "", quantity: "1" };
    const selectedVariant = product.variants.find((variant) => String(variant.id) === String(selection.variantId));
    const quantity = Number(selection.quantity || 0);

    if (!selectedVariant) {
      showToast({ message: "Please select a variant before placing the order.", variant: "warning" });
      return;
    }

    if (!quantity || quantity < 1) {
      showToast({ message: "Quantity must be at least 1.", variant: "warning" });
      return;
    }

    if (quantity > Number(selectedVariant.stock || 0)) {
      showToast({ message: "Selected quantity exceeds available stock.", variant: "warning" });
      return;
    }

    addCartItem({
      product_id: product.id,
      product_name: product.name,
      product_image_url: product.image_url,
      variant_id: selectedVariant.id,
      variant_name: selectedVariant.variant_name,
      variant_image_url: selectedVariant.image_url || "",
      sku: selectedVariant.sku,
      stock: Number(selectedVariant.stock || 0),
      quantity
    });

    showToast({ message: "Added to cart.", variant: "success" });
  };

  return (
    <div className="py-4">
      <div className="mb-4 px-2">
        <h3 className="mb-1">Products</h3>
        <p className="text-muted mb-3 small">{totalProducts} products available</p>
        <InputGroup size="sm">
          <InputGroup.Text>
            <FontAwesomeIcon icon={faBoxOpen} />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </InputGroup>
      </div>
      {loading ? (
        <Row className="g-3 vendor-products-row">
          {[...Array(6)].map((_, i) => (
            <Col xs={6} md={3} lg={3} xl={2} className="mb-3" key={i}>
              <ProductCardSkeleton />
            </Col>
          ))}
        </Row>
      ) : products.length === 0 ? (
        <Card border="light" className="shadow-sm">
          <Card.Body className="text-center py-5 text-muted">
            No products available.
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row className="g-3 vendor-products-row px-2">
            {productsWithVariants.map((product) => {
              const selection = orderSelections[product.id] || buildInitialSelection(product.variants);
              const selectedVariant = product.variants.find((variant) => String(variant.id) === String(selection.variantId)) || null;

              return (
                <Col xs={6} md={3} lg={3} xl={2} className="mb-3" key={product.id}>
                  <Card border="light" className="shadow-sm h-100 vendor-product-card overflow-hidden">
                    <div className="vendor-product-image-wrapper" onClick={() => history.push(Routes.ProductDetails.path.replace(":id", product.id))} style={{ cursor: "pointer" }}>
                      {product.image_url ? (
                        <img
                          src={resolveAssetUrl(product.image_url)}
                          alt={product.name}
                          className="vendor-product-image"
                        />
                      ) : (
                        <div className="vendor-product-image-placeholder d-flex align-items-center justify-content-center text-muted">
                          <FontAwesomeIcon icon={faBoxOpen} size="2x" />
                        </div>
                      )}
                    </div>
                    <Card.Body className="p-2">
                      <h6 className="mb-1 text-truncate" onClick={() => history.push(Routes.ProductDetails.path.replace(":id", product.id))} style={{ cursor: "pointer" }}>{product.name}</h6>
                      <div className="small text-muted mb-2">
                        {product.variants.length} variant{product.variants.length !== 1 ? "s" : ""}
                      </div>
                      {product.variants.length > 0 && (
                        <Form.Select
                          size="sm"
                          className="mb-2"
                          value={selection.variantId}
                          onChange={(event) => updateSelection(product.id, "variantId", event.target.value)}
                        >
                          <option value="">Select a variant</option>
                          {product.variants.map((variant) => (
                            <option key={variant.id} value={variant.id}>
                              {variant.variant_name} ({variant.stock || 0} in stock)
                            </option>
                          ))}
                        </Form.Select>
                      )}
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Control
                          type="text"
                          inputMode="numeric"
                          size="sm"
                          placeholder="Qty"
                          className="w-50"
                          value={selection.quantity ?? "1"}
                          onChange={(event) => {
                            const nextValue = sanitizeQuantityInput(event.target.value);
                            updateSelection(product.id, "quantity", nextValue);
                          }}
                          disabled={!selectedVariant}
                        />
                        <Button
                          variant="primary"
                          size="sm"
                          className="w-50"
                          onClick={() => handleAddToCart(product)}
                          disabled={!selectedVariant}
                        >
                          <FontAwesomeIcon icon={faCartPlus} />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
          {loadingMore && (
            <Row className="g-3 vendor-products-row">
              {[...Array(2)].map((_, i) => (
                <Col xs={6} lg={6} xl={4} className="mb-3" key={`loading-${i}`}>
                  <ProductCardSkeleton />
                </Col>
              ))}
            </Row>
          )}
        </>
      )}
    </div>
  );
};